import React, { useEffect, useMemo, useState } from "react";
import { notification, Radio, Slider, Form } from "antd";
import { SyncOutlined, SaveOutlined } from "@ant-design/icons";
import axios from "axios";
import { XThemeSelect, ThemeInput } from "../../Styles/Theme";
import { AudienceGrid, AudienceField, SaveRegenerateButton } from "./QuestionPreview.style";
import {
  FormWrapper,
  SubCard,
  SubCardHeader,
  SubCardTitle,
  SubCardBody,
  AgeRangeHeader,
  AgeSliderWrapper,
  TickMarksRuler,
  TickMark,
  InlineRadioGroup,
} from "./TargetAudienceForm.style";
import config from "../../../config";
import { getUserToken } from "../../../utils/helpers";

const countryOptions = [
  { label: 'United States', value: 'US' },
];

const languageOptions = [
  { label: 'English', value: 'English' },
  { label: 'Spanish', value: 'Spanish' },
  { label: 'French', value: 'French' },
  { label: 'German', value: 'German' },
  { label: 'Chinese', value: 'Chinese' },
];

export const normalizeDemographics = (demographics) => ({
  country: demographics?.country || 'US',
  language: demographics?.language || 'English', // Default to English
  ageRange: {
    min: demographics?.ageRange?.min ?? 18, // Default to 18
    max: demographics?.ageRange?.max ?? 99, // Default to 99
  },
  gender: demographics?.gender || [],
  race: demographics?.race || [],
  state: demographics?.state || [],
  dma: demographics?.dma || [],
  topic: demographics?.topic || "",
});

const getInitialLocationFilter = (demographics) => {
  if ((demographics?.state || []).length > 0) return 'state';
  if ((demographics?.dma || []).length > 0) return 'city';
  return 'none';
};

const TargetAudienceForm = ({
  demographics,
  onChange,
  isSaving,
  showHeader = true,
  onSave,
  onSaveAndRegenerate,
  isRegenerating,
}) => {
  const [form] = Form.useForm();
  const memoizedDefaults = useMemo(() => normalizeDemographics(demographics), [demographics]);
  const [formValues, setFormValues] = useState(memoizedDefaults);
  const [locationFilter, setLocationFilter] = useState(() => getInitialLocationFilter(demographics));

  // Track if user has made changes to the form
  const hasLocalChanges = useMemo(() => {
    return JSON.stringify(formValues) !== JSON.stringify(memoizedDefaults);
  }, [formValues, memoizedDefaults]);
  const [demographicOptions, setDemographicOptions] = useState({
    gender: [],
    race: [],
    state: [],
    dma: [],
  });

  useEffect(() => {
    setFormValues(memoizedDefaults);
    setLocationFilter(getInitialLocationFilter(demographics));
    // Sync with Ant Design Form
    form.setFieldsValue(memoizedDefaults);
  }, [memoizedDefaults, demographics, form]);

  useEffect(() => {
    const fetchDemographicOptions = async () => {
      try {
        const response = await axios.get(`${config.api.base_url}/campaigns/demographic-options`, {
          headers: {
            Authorization: 'Bearer ' + getUserToken(),
          },
        });
        if (response.data && response.data.status === 1) {
          const data = response.data.data;
          setDemographicOptions({
            gender: data.gender?.options || [],
            race: data.race?.options || [],
            state: data.state?.options || [],
            dma: data.dma?.options || [],
          });
        }
      } catch (error) {
        console.error('Error fetching demographic options:', error);
        notification.error({
          message: 'Error',
          description: 'Failed to load demographic options'
        });
      }
    };
    fetchDemographicOptions();
  }, []);

  const handleChange = (field, value) => {
    const updated = {
      ...formValues,
      [field]: value,
    };
    setFormValues(updated);
    onChange?.(updated);
  };

  const handleAgeRangeSlider = (value) => {
    const updated = {
      ...formValues,
      ageRange: {
        min: value[0],
        max: value[1],
      },
    };
    setFormValues(updated);
    onChange?.(updated);
  };

  // Slider value - defaults to 18-90
  const ageSliderValue = [
    formValues.ageRange.min,
    formValues.ageRange.max
  ];

  const handleLocationFilterChange = (filterType) => {
    setLocationFilter(filterType);
    const updated = {
      ...formValues,
      state: [],
      dma: [],
    };
    setFormValues(updated);
    onChange?.(updated);
  };

  return (
    <Form form={form} layout="vertical" initialValues={memoizedDefaults}>
    <FormWrapper>
      {/* Basic Details Sub-Card */}
      <SubCard>
        <SubCardHeader>
          <SubCardTitle>Basic Details</SubCardTitle>
        </SubCardHeader>
        <SubCardBody>
          <AudienceGrid>
            {/* Topic */}
            <AudienceField>
              <Form.Item
                name="topic"
                label={<span className="fieldLabel">Topic</span>}
              >
                <ThemeInput
                  className="audienceInput"
                  size="large"
                  placeholder="Enter topic"
                  onChange={(e) => handleChange("topic", e.target.value)}
                  disabled={isSaving}
                />
              </Form.Item>
            </AudienceField>

            {/* Language */}
            <AudienceField>
              <Form.Item
                name="language"
                label={<span className="fieldLabel">Language</span>}
              >
                <XThemeSelect
                  className="fullWidth noRadius audienceSelect"
                  size="large"
                  placeholder="Select language"
                  onChange={(value) => handleChange("language", value)}
                  options={languageOptions}
                  disabled={isSaving}
                />
              </Form.Item>
            </AudienceField>
          </AudienceGrid>
        </SubCardBody>
      </SubCard>

      {/* Who are you trying to reach? Sub-Card */}
      <SubCard>
        <SubCardHeader>
          <SubCardTitle>Who are you trying to reach?</SubCardTitle>
        </SubCardHeader>
        <SubCardBody>
          <AudienceGrid>
            {/* Country */}
            <AudienceField>
              <Form.Item
                name="country"
                label={<span className="fieldLabel">Country</span>}
              >
                <XThemeSelect
                  className="fullWidth noRadius audienceSelect"
                  size="large"
                  placeholder="Select country"
                  onChange={(value) => handleChange("country", value)}
                  options={countryOptions}
                  disabled={isSaving}
                />
              </Form.Item>
            </AudienceField>

            {/* Location Filter Toggle */}
            <AudienceField>
              <span className="fieldLabel">Filter by Location</span>
              <InlineRadioGroup>
                <Radio.Group
                  value={locationFilter}
                  onChange={(e) => handleLocationFilterChange(e.target.value)}
                  disabled={isSaving}
                >
                  <Radio value="none">None</Radio>
                  <Radio value="state">State</Radio>
                  <Radio value="city">City</Radio>
                </Radio.Group>
              </InlineRadioGroup>
            </AudienceField>

            {/* State or City based on selection */}
            {locationFilter === 'state' && (
              <AudienceField>
                <span className="fieldLabel">State</span>
                <XThemeSelect
                  className="fullWidth noRadius audienceSelect"
                  size="large"
                  mode="multiple"
                  value={formValues.state || []}
                  placeholder="Select State"
                  onChange={(value) => handleChange("state", value)}
                  options={demographicOptions.state}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  disabled={isSaving}
                />
              </AudienceField>
            )}
            {locationFilter === 'city' && (
              <AudienceField>
                <span className="fieldLabel">City</span>
                <XThemeSelect
                  className="fullWidth noRadius audienceSelect"
                  size="large"
                  mode="multiple"
                  value={formValues.dma || []}
                  placeholder="Select City"
                  onChange={(value) => handleChange("dma", value)}
                  options={demographicOptions.dma}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                  }
                  disabled={isSaving}
                />
              </AudienceField>
            )}

            {/* Age Range Slider */}
            <AudienceField>
              <AgeRangeHeader>
                <span className="fieldLabel">Age Range</span>
                <span className="ageValue">{ageSliderValue[0]} - {ageSliderValue[1]}</span>
              </AgeRangeHeader>
              <AgeSliderWrapper>
                <Slider
                  range
                  min={13}
                  max={99}
                  value={ageSliderValue}
                  onChange={handleAgeRangeSlider}
                  disabled={isSaving}
                  tooltip={{ formatter: (value) => value }}
                />
                <TickMarksRuler>
                  {Array.from({ length: 87 }, (_, i) => {
                    const age = 13 + i;
                    const isMajor = age % 10 === 0;
                    return (
                      <TickMark key={age} $isMajor={isMajor}>
                        {isMajor && <span className="label">{age}</span>}
                      </TickMark>
                    );
                  })}
                </TickMarksRuler>
              </AgeSliderWrapper>
            </AudienceField>

            {/* Gender */}
            <AudienceField>
              <Form.Item
                name="gender"
                label={<span className="fieldLabel">Gender</span>}
              >
                <XThemeSelect
                  className="fullWidth noRadius audienceSelect"
                  size="large"
                  mode="multiple"
                  placeholder="Select Gender"
                  onChange={(value) => handleChange("gender", value)}
                  options={demographicOptions.gender}
                  disabled={isSaving}
                />
              </Form.Item>
            </AudienceField>

            {/* Race / Ethnicity */}
            <AudienceField>
              <Form.Item
                name="race"
                label={<span className="fieldLabel">Race / Ethnicity</span>}
              >
                <XThemeSelect
                  className="fullWidth noRadius audienceSelect"
                  size="large"
                  mode="multiple"
                  placeholder="Select Race / Ethnicity"
                  onChange={(value) => handleChange("race", value)}
                  options={demographicOptions.race}
                  disabled={isSaving}
                />
              </Form.Item>
            </AudienceField>
          </AudienceGrid>
        </SubCardBody>
      </SubCard>

      {/* Show Save buttons only when user has made changes */}
      {hasLocalChanges && (
        <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', width: '100%', marginTop: '-12px' }}>
          {onSave && (
            <SaveRegenerateButton
              onClick={onSave}
              disabled={isRegenerating || isSaving}
              style={{ background: '#f5f5f5', color: '#333', border: '1px solid #d9d9d9', margin: 0 }}
            >
              {isSaving && !isRegenerating ? <SaveOutlined spin /> : <SaveOutlined />}
              {isSaving && !isRegenerating ? 'Saving...' : 'Save'}
            </SaveRegenerateButton>
          )}
          {onSaveAndRegenerate && (
            <SaveRegenerateButton
              onClick={onSaveAndRegenerate}
              disabled={isRegenerating || isSaving}
              style={{ margin: 0 }}
            >
              {isRegenerating ? <SyncOutlined spin /> : <SyncOutlined />}
              {isRegenerating ? 'Regenerating...' : 'Save & Regenerate'}
            </SaveRegenerateButton>
          )}
        </div>
      )}
    </FormWrapper>
    </Form>
  );
};

export default TargetAudienceForm;
